<template>
  <v-container fluid class="pa-0">
    <!-- Flash Notice Snackbar -->
    <v-snackbar
      v-model="showSnackbar"
      :timeout="15000"
      color="success"
      location="top"
      multi-line
    >
      <div class="d-flex align-center">
        <v-icon class="mr-3">mdi-check-circle</v-icon>
        {{ flashNotice }}
      </div>
      <template v-slot:actions>
        <v-btn variant="text" @click="showSnackbar = false">
          Close
        </v-btn>
      </template>
    </v-snackbar>

    <!-- Search Section -->
    <div id="home-search">
      <v-row justify="center" class="search-row">
        <v-col cols="12" sm="10" md="8" class="search-col">
          <v-card
            flat
            class="search-card"
          >
            <v-card-text class="search-card-text">
              <h1 class="text-center text-h5 font-weight-medium mb-6 search-title">
                {{ t.how_can_we_help }}
              </h1>

              <v-form @submit.prevent="submitSearch" class="search-form">
                <div class="search-input-wrapper">
                  <v-text-field
                    v-model="searchQuery"
                    :placeholder="t.search_placeholder"
                    variant="outlined"
                    hide-details
                    bg-color="white"
                    base-color="rgb(206, 216, 224)"
                    color="rgb(206, 216, 224)"
                    density="comfortable"
                    class="search-field"
                  >
                  </v-text-field>

                  <v-btn
                    type="submit"
                    class="search-button"
                    :style="{ backgroundColor: buttonColor }"
                    elevation="0"
                    density="comfortable"
                    icon="mdi-magnify"
                  ></v-btn>
                </div>
              </v-form>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </div>

    <!-- Featured Categories Section -->
    <div v-if="hasCategories" id="featured-categories" class="categories-section">
      <h2 class="text-center text-h6 space-h2">
        {{ t.browse_topics }}
      </h2>

      <v-container class="categories-container">
        <v-row id="home-topics">
          <v-col
            v-for="category in categories"
            :key="category.id"
            cols="12"
            sm="6"
            md="4"
            lg="3"
            class="topic-box-col"
          >
            <a
              :href="category.path"
              class="topic-box-link"
              :id="`category-${category.id}`"
            >
              <div class="topic-card-wrapper">
                <v-card
                  class="topic-card"
                >
                <v-card-text class="text-center topic-card-text">
                  <v-icon
                    :icon="category.icon.startsWith('mdi-') ? category.icon : `mdi-${category.icon}`"
                    size="48"
                    color="#666"
                    class="category-icon"
                  ></v-icon>

                  <h5 class="text-h5 category-header">
                    {{ category.name }}
                  </h5>

                  <p class="text-body-2 text-center less-important">
                    {{ category.meta_description }}
                  </p>
                </v-card-text>
              </v-card>
              </div>
            </a>
          </v-col>
        </v-row>
      </v-container>
    </div>
  </v-container>
</template>

<script>
export default {
  name: 'HomePage',
  props: {
    // Props passed from Rails via data attributes
    categoriesData: {
      type: Array,
      default: () => []
    },
    translations: {
      type: Object,
      default: () => ({})
    },
    resultPath: {
      type: String,
      default: '/result'
    },
    buttonColor: {
      type: String,
      default: '#0275d8'
    },
    flashNotice: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      searchQuery: '',
      categories: [],
      showSnackbar: false
    }
  },
  computed: {
    t() {
      return {
        how_can_we_help: this.translations.how_can_we_help || 'How can we help?',
        search_placeholder: this.translations.search_placeholder || 'Enter a keyword, topic, or question',
        browse_topics: this.translations.browse_topics || 'Browse Topics'
      }
    },
    hasCategories() {
      return this.categories.length > 0
    }
  },
  mounted() {
    // Parse categories data from Rails
    this.categories = this.categoriesData

    // Show flash notice if present
    if (this.flashNotice) {
      this.showSnackbar = true
    }
  },
  methods: {
    submitSearch() {
      if (this.searchQuery.trim()) {
        const form = document.createElement('form')
        form.method = 'GET'
        form.action = this.resultPath

        const input = document.createElement('input')
        input.type = 'hidden'
        input.name = 'q'
        input.value = this.searchQuery
        form.appendChild(input)

        document.body.appendChild(form)
        form.submit()
        document.body.removeChild(form)
      }
    }
  }
}
</script>

<style scoped>
/* ==================== SEARCH SECTION ==================== */

/* Search row spacing */
.search-row {
  margin-top: 40px;
  margin-bottom: 40px;
}

.search-col {
  padding: 0 12px !important;
}

.search-card {
  border: 1px solid rgb(206, 216, 224) !important;
  border-radius: 8px !important;
}

.search-card-text {
  padding: 40px !important;
}

/* Search title */
.search-title {
  margin-bottom: 30px !important;
}

/* Search form */
.search-form {
  margin: 0;
}

/* Search input wrapper */
.search-input-wrapper {
  display: flex;
  align-items: center;
  gap: 0;
}

.search-field {
  flex: 1;
}

.search-field :deep(.v-text-field__details) {
  display: none;
}

.search-field :deep(.v-field) {
  border-radius: 8px 0 0 8px !important;
  border: 1px solid rgb(206, 216, 224) !important;
  font-size: 16px;
}

.search-field :deep(.v-field__input) {
  padding: 12px 16px !important;
  min-height: 48px !important;
}

/* Search button */
.search-button {
  border-radius: 0 8px 8px 0 !important;
  border: 1px solid rgb(206, 216, 224);
  border-left: none !important;
  min-width: 56px !important;
  height: 48px !important;
  color: white !important;
  transition: background-color 0.2s;
}

.search-button:hover {
  opacity: 0.85;
}

/* ==================== CATEGORIES SECTION ==================== */

.categories-section {
  padding-bottom: 40px;
}

.categories-container {
  max-width: 1200px;
  padding: 0 !important;
}

/* Browse topics heading */
.space-h2 {
  margin-top: 24px;
  margin-bottom: 32px;
}

/* Topic cards column */
.topic-box-col {
  padding: 8px !important;
}

.topic-box-link {
  text-decoration: none;
  display: block;
  height: 100%;
}

.topic-card-wrapper {
  padding-top: 2px;
  height: 100%;
}

.topic-card {
  border: 1px solid rgb(206, 216, 224) !important;
  border-radius: 15px !important;
  height: 100%;
  position: relative;
  overflow: hidden;
  transition: transform 0.15s ease, box-shadow 0.15s ease;
}

.topic-card:hover {
  transform: translateY(-2px);
  box-shadow: var(--v-shadow-4);
}

.topic-card-text {
  padding: 40px 20px !important;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 200px;
}

/* Category icon */
.category-icon {
  display: block;
  margin-bottom: 16px !important;
  font-size: 48px;
  color: #666;
}

/* Category header */
.category-header {
  color: #333;
  font-size: 16px;
  font-weight: 500;
  margin: 0 0 8px 0;
  text-align: center;
}

/* Category description */
.less-important {
  color: #666;
  font-size: 14px;
  line-height: 1.5;
  margin: 0;
}

/* ==================== RESPONSIVE BREAKPOINTS ==================== */

/* Extra small devices (phones, less than 600px) */
@media (max-width: 599px) {
  .search-row {
    margin-top: 24px;
    margin-bottom: 24px;
  }

  .search-col {
    padding: 0 8px !important;
  }

  .search-card-text {
    padding: 24px 20px !important;
  }

  .search-title {
    font-size: 24px !important;
    margin-bottom: 20px !important;
  }

  .search-field :deep(.v-field__input) {
    padding: 12px 16px !important;
    font-size: 16px !important;
    min-height: 48px !important;
  }

  .search-button {
    min-width: 48px !important;
    height: 48px !important;
  }

  /* Categories section */
  .categories-section {
    padding-bottom: 24px;
  }

  .space-h2 {
    margin-top: 16px;
    margin-bottom: 20px;
  }

  .topic-box-col {
    padding: 6px !important;
  }

  .topic-card-text {
    padding: 32px 16px !important;
    min-height: 160px;
  }

  .category-icon {
    margin-bottom: 12px !important;
  }
}

/* Small devices (landscape phones, 600px and up) */
@media (min-width: 600px) and (max-width: 959px) {
  .search-col {
    padding: 0 12px !important;
  }

  .search-card-text {
    padding: 32px 24px !important;
  }

  .topic-box-col {
    padding: 8px !important;
  }

  .topic-card-text {
    padding: 36px 20px !important;
  }
}

/* Medium devices (tablets, 960px and up) */
@media (min-width: 960px) and (max-width: 1263px) {
  .topic-box-col {
    padding: 8px !important;
  }

  .topic-card-text {
    padding: 36px 24px !important;
  }
}

/* Large devices (desktops, 1264px and up) */
@media (min-width: 1264px) {
  .topic-box-col {
    padding: 8px !important;
  }
}
</style>
